import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import AppErrorBoundary from "./component/AppErrorBoundary";
import AppVersionGuard from "./component/AppVersionGuard";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./context/useAuth";
import { AdminAlertCounterProvider } from "./context/adminAlertCounters";
import { logAdminDiagnostic } from "./utils/clientDiagnostics";

const RESUME_REFRESH_AFTER_MS = 2 * 1000;

function BrowserResumeRecovery({ children }) {
  const hiddenAtRef = useRef(0);
  const lastRecoveryAtRef = useRef(0);

  useEffect(() => {
    const recoverPage = (reason = "unknown", hiddenDuration = 0) => {
      const now = Date.now();
      if (now - lastRecoveryAtRef.current < 2000) return;
      lastRecoveryAtRef.current = now;

      logAdminDiagnostic("app-resume", {
        reason,
        hiddenDurationMs: Math.max(0, Number(hiddenDuration) || 0),
      });

      // Keep the current UI mounted. A full remount discards useful cached
      // data and can leave Safari waiting on requests that were suspended in
      // the background. Pages can refresh their data from this event instead.
      window.dispatchEvent(
        new CustomEvent("cinema:resume", {
          detail: { resumedAt: now },
        }),
      );
      document.documentElement.classList.add("app-resuming");
      window.requestAnimationFrame(() => {
        document.documentElement.classList.remove("app-resuming");
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenDuration = hiddenAtRef.current
        ? Date.now() - hiddenAtRef.current
        : 0;
      hiddenAtRef.current = 0;

      if (hiddenDuration >= RESUME_REFRESH_AFTER_MS) {
        recoverPage("visibilitychange", hiddenDuration);
      }
    };

    const handlePageShow = (event) => {
      if (event.persisted) recoverPage("pageshow-persisted");
    };

    const handlePageHide = () => {
      hiddenAtRef.current = Date.now();
    };

    const handleFocus = () => {
      if (document.visibilityState === "hidden" || !hiddenAtRef.current) return;

      const hiddenDuration = Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = 0;

      if (hiddenDuration >= RESUME_REFRESH_AFTER_MS) {
        recoverPage("focus", hiddenDuration);
      }
    };

    const handleOnline = () => recoverPage("online");

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  return children;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <BrowserResumeRecovery>
      <AppVersionGuard>
        <AuthProvider>
          <AppErrorBoundary>
            <AdminAlertCounterProvider>
              <App />
            </AdminAlertCounterProvider>
          </AppErrorBoundary>
        </AuthProvider>
      </AppVersionGuard>
    </BrowserResumeRecovery>
  </BrowserRouter>,
);

// ถอน service worker/caches จาก PWA เวอร์ชันที่เคยปล่อยไป
// หลังจากโหลดเวอร์ชันนี้แล้ว เว็บไซต์จะใช้งานผ่าน browser ตามปกติ
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
}

if ("caches" in window) {
  caches.keys().then((keys) => {
    keys
      .filter((key) => key.startsWith("cinema-admin-"))
      .forEach((key) => caches.delete(key));
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
